import type { ComputedRef, Ref } from 'vue';
import {
  computed,
  onMounted,
  ref,
} from 'vue';

import type { Permission } from '@modules/permissions/utils';
import type { ReportType } from '@modules/reports/enums';
import type {
  IReportInnerTabItem,
  IReportQueryOptions,
  IReportTabItem,
  IReportTabsResponse,
} from '@modules/reports/types';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { ReportWidgetType } from '@modules/reports/enums';
import {
  useReportAddTabMutation,
  useReportRemoveTabMutation,
} from '@modules/reports/mutations';
import { useReportTabsQuery } from '@modules/reports/queries';
import { isBoolean } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type { IReportWidgetEmits } from '../types';

export interface ReportWidgetComposable {
  queryOptions: ComputedRef<IReportQueryOptions>;
  tabsData: Ref<IReportTabsResponse | undefined>;
  tabsList: Ref<IReportTabItem[]>;
  innerTabsList: Ref<IReportTabItem[]>;
  activeTab: Ref<IReportTabItem | undefined>;
  activeInnerTab: Ref<IReportTabItem | undefined>;
  isStructureStatesAllowed: Ref<boolean>;
  addTabBtnShortCode: Ref<string>;
  isTabsLoading: Ref<boolean | undefined>;
  isAddTabLoading: Ref<boolean | undefined>;
  isRemoveTabLoading: Ref<boolean | undefined>;
  isSegmentsType: ComputedRef<boolean>;
  innerTabFetching: Ref<boolean | undefined>;
  onSelectTab(id: number): void;
  onSelectInnerTab(id: number): void;
  onToggleTab(id: number): void;
  onRemoveTab(id: number): void;
  onAddTab(): void;
  changeTabName(name?: string): void;
  changeStructureState(state?: string): void;
  onInnerTabFetching(isInnerTabFetching: boolean): void;
}

export default function useReportWidget(
  reportType: Ref<ReportType>,
  widgetType: Ref<ReportWidgetType>,
  defaultInnerTabs: Ref<IReportInnerTabItem[] | undefined>,
  queryPermission: Ref<Permission>,
  structureStatesPermission: Ref<Permission | undefined>,
  translationKey: Ref<symbol>,
  emit: IReportWidgetEmits,
): ReportWidgetComposable {
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();
  const { confirmDelete } = useConfirmDialogs();

  const tabsList = ref<IReportTabItem[]>([]);
  const innerTabsList = ref<IReportTabItem[]>([]);
  const activeTabId = ref(0);
  const activeInnerTabId = ref<number>();
  const innerTabFetching = ref<boolean>();

  const activeTab = computed<IReportTabItem | undefined>(() => tabsList.value.find((item) => item.id === activeTabId.value));

  const activeInnerTab = computed<IReportTabItem | undefined>(() => innerTabsList.value.find((item) => item.id === activeInnerTabId.value));

  const isStructureStatesAllowed = computed<boolean>(() => (
    !!structureStatesPermission.value
    && hasPermission(structureStatesPermission.value).value
    && !activeTab.value?.read_only
  ));

  const isSegmentsType = computed<boolean>(() => widgetType.value === ReportWidgetType.SEGMENTS);

  const emptyTabNameShortCode = computed<string>(() => (isSegmentsType.value ? 'REPORTS.SEGMENTS_NO_NAME' : 'REPORTS.STRUCTURES_NO_NAME'));
  const defaultTabNameShortCode = computed<string>(() => (isSegmentsType.value ? 'REPORTS.SEGMENTS_ALL_PLAYERS' : 'REPORTS.STRUCTURES_ALL_COLUMNS'));
  const addTabBtnShortCode = computed<string>(() => (isSegmentsType.value ? 'REPORTS.SEGMENTS_ADD_NEW_ELEMENT' : 'REPORTS.STRUCTURES_ADD_NEW_ELEMENT'));

  const queryOptions = computed<IReportQueryOptions>(() => ({
    permission: queryPermission.value,
    widgetType: widgetType.value,
    reportType: reportType.value,
  }));

  const { data: tabsData, isFetching: isTabsLoading } = useReportTabsQuery(queryOptions);

  const { mutateAsync: fetchAddTab, isLoading: isAddTabLoading } = useReportAddTabMutation();
  const { mutateAsync: fetchRemoveTab, isLoading: isRemoveTabLoading } = useReportRemoveTabMutation();

  watchImmediate(tabsData, () => {
    tabsList.value = [
      {
        id: 0,
        name: null,
        shortcode: defaultTabNameShortCode.value,
        allowExpand: false,
        allowRemove: false,
        isActive: activeTabId.value === 0,
      },
      ...tabsData.value?.list?.map((item) => ({
        ...item,
        name: item.name,
        shortcode: emptyTabNameShortCode.value,
        expanded: false,
        allowExpand: true,
        allowRemove: isBoolean(item.read_only) ? !item.read_only : true,
        isActive: item.id === activeTabId.value,
      })) ?? [],
    ].sort((a, b) => (b.id === 0 ? 0 : a.id - b.id));

    innerTabsList.value = defaultInnerTabs.value?.filter(({ id }) => tabsData.value?.tabs_list?.includes(id)).map((item) => ({
      ...item,
      type: activeInnerTabId.value === item.id ? 'secondary' : 'transparent-secondary',
      allowExpand: false,
      allowRemove: false,
      expanded: false,
      name: null,
      shortcode: item.shortcode,
      isActive: activeInnerTabId.value === item.id,
      noBorder: true,
    })) ?? [];
  }, { deep: true });

  function selectTab(list: IReportTabItem[], id: number): IReportTabItem[] {
    return list.map((item) => ({
      ...item,
      isActive: item.id === id,
    }));
  }

  function toggleTab(list: IReportTabItem[], id: number): IReportTabItem[] {
    return list.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          expanded: !item.expanded,
        };
      }
      return item;
    });
  }

  function removeTab(id: number): void {
    tabsList.value = tabsList.value.filter((item) => item.id !== id);

    if (activeTabId.value === id) {
      onSelectTab(0);
    }
  }

  function addTab(id: number): void {
    tabsList.value.push({
      id,
      name: null,
      shortcode: emptyTabNameShortCode.value,
      expanded: false,
      isActive: true,
      allowExpand: true,
      allowRemove: true,
    });

    onSelectTab(id);
  }

  function changeTabName(name: string): void {
    tabsList.value = tabsList.value.map((item) => {
      if (item.id === activeTabId.value) {
        return {
          ...item,
          name,
        };
      }
      return item;
    });
  }

  function changeStructureState(state: string): void {
    tabsList.value = tabsList.value.map((item) => {
      if (item.id === activeTabId.value) {
        return {
          ...item,
          aasm_state: state,
        };
      }
      return item;
    });
  }

  function changeTabRemoveIcon(id: number, isLoading: boolean): void {
    tabsList.value = tabsList.value.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          removeIcon: isLoading ? 'pi pi-spin pi-spinner' : undefined,
          noPointerEvents: isLoading,
        };
      }
      return item;
    });
  }

  function onSelectTab(id: number): void {
    activeTabId.value = id;
    tabsList.value = selectTab(tabsList.value, id);
    emitChangeTab(id);
  }

  function emitChangeTab(id: number): void {
    emit('select-tab', id);
  }

  function onSelectInnerTab(id: number): void {
    activeInnerTabId.value = id;
    innerTabsList.value = selectTab(innerTabsList.value, id);
  }

  function onToggleTab(id: number): void {
    tabsList.value = toggleTab(tabsList.value, id);
  }

  function onRemoveTab(id: number, name?: string): void {
    confirmDelete({
      group: 'confirmDialog',
      header: getTranslation(translationKey.value, 'REPORTS.DIALOG_TITLE').value,
      message: getTranslation(translationKey.value, 'REPORTS.DIALOG_QUESTION', { name: name || id }).value,
      acceptLabel: getTranslation(translationKey.value, 'REPORTS.DIALOG_YES').value,
      rejectLabel: getTranslation(translationKey.value, 'REPORTS.DIALOG_NO').value,
      accept: () => {
        changeTabRemoveIcon(id, true);
        void fetchRemoveTab({ options: queryOptions.value, tabId: id })
          .then(() => removeTab(id))
          .finally(() => changeTabRemoveIcon(id, false));
      },
    });
  }

  function onAddTab(): void {
    void fetchAddTab(queryOptions.value).then((data) => addTab(data.structure?.id || data.segment.id));
  }

  function onInnerTabFetching(isInnerTabFetching: boolean): void {
    innerTabFetching.value = isInnerTabFetching;
  }

  onMounted(() => {
    emitChangeTab(activeTabId.value);
  });

  return {
    queryOptions,
    tabsData,
    tabsList,
    innerTabsList,
    activeTab,
    activeInnerTab,
    isStructureStatesAllowed,
    addTabBtnShortCode,
    isTabsLoading,
    isAddTabLoading,
    isRemoveTabLoading,
    isSegmentsType,
    innerTabFetching,
    onSelectTab,
    onSelectInnerTab,
    onToggleTab,
    onRemoveTab,
    onAddTab,
    changeTabName,
    changeStructureState,
    onInnerTabFetching,
  };
}
